"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Pre-login "Ask Amy" teaser (item 6 + 7): the header pill opens this
 * popover instead of a chat Amy can't actually run pre-account. Copy is
 * modeled on Google Health's "Ask Coach" pattern — concrete, warm,
 * example prompts a person would actually type — replacing main.html's
 * meaningless `.amy-context` line ("Tuesday dinner · food at home · 25
 * minutes available"). Picking a prompt opens sign-up: Amy only runs
 * against a real account and real plan/pantry data, never fabricated
 * pre-login.
 */

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { AmyPillButton } from "@/components/ui/amy-pill-button"
import { useI18n } from "@/i18n"
import type { AuthMode } from "@/components/auth/authMode"

interface AskAmyTeaserProps {
  onOpenAuth: (mode: AuthMode) => void
  compact?: boolean
}

export function AskAmyTeaser({ onOpenAuth, compact = false }: AskAmyTeaserProps) {
  const { t } = useI18n()

  const prompts = [
    t("landingPage.walkthrough.amyPrompt1"),
    t("landingPage.walkthrough.amyPrompt2"),
    t("landingPage.walkthrough.amyPrompt3"),
  ]

  return (
    <Popover>
      <PopoverTrigger asChild>
        <AmyPillButton label={t("amy.askAmy")} compact={compact} />
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 rounded-bb-xl border-line bg-paper p-5 shadow-[var(--bb-shadow)]"
      >
        <p className="text-xs font-bold tracking-wide text-green">
          {t("landingPage.walkthrough.amyTitle")}
        </p>
        <p className="mt-1 text-sm text-ink-2">{t("landingPage.walkthrough.amyBody")}</p>

        <div className="mt-4 flex flex-col gap-2">
          {prompts.map(prompt => (
            <button
              key={prompt}
              type="button"
              onClick={() => onOpenAuth("signUp")}
              className="min-h-11 rounded-bb-md border border-line bg-cream px-3 py-2 text-left text-sm text-ink transition-colors hover:border-green/50 hover:bg-lime/20"
            >
              {prompt}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}
